// Shared, side-effect-free theme application used by both the pre-paint
// theme-bootstrap IIFE and the runtime theme toggle. Kept in its own module so
// importing it never re-runs theme-bootstrap's initial-apply IIFE.

export type ThemeName = "light" | "dark";

// The `--color-background-900` values from the AFAS theme CSS
// (afas-theme.css / afas-theme-dark.css), mirrored here so the document root can be
// painted the correct color BEFORE that CSS bundle loads.
export const THEME_BACKGROUND: Record<ThemeName, string> = {
  light: "#f8fafc",
  dark: "#1e1f20",
};

// Cookie name the server reads (see _LayoutAiChat.cshtml) to render the initial
// color-scheme + data-theme, so a refresh does not flash white before the theme script runs.
export const THEME_COOKIE = "help.theme";

/**
 * Applies the theme to the document root: the `data-theme` attribute the app reads, plus
 * an inline `background-color` and `color-scheme`. The latter two matter for full page
 * navigations (e.g. the language switch, which reloads the page): without them the browser
 * paints its default white canvas for the blank inter-page frame before the AFAS theme CSS
 * loads, a jarring white flash that is worst in dark mode and in Firefox (issue #116).
 * Because theme-bootstrap sets these before first paint on every page, the page being
 * navigated away from already carries the dark canvas, so the transition frame stays dark.
 */
export function applyThemeToDocument(theme: ThemeName, root: HTMLElement): void {
  root.setAttribute("data-theme", theme);
  root.style.backgroundColor = THEME_BACKGROUND[theme];
  root.style.colorScheme = theme;
  // Persist the resolved theme in a readable cookie so the SERVER can render the matching
  // color-scheme + data-theme in the initial HTML on the next load. That paints the correct
  // canvas on a refresh before this (external) script is even fetched — otherwise Firefox
  // briefly shows a white background in dark mode (issue #116).
  if (typeof document !== "undefined") {
    // Only mark the cookie Secure over HTTPS. The dev server also binds http://localhost:5000
    // (Properties/launchSettings.json); browsers drop a Secure cookie on http origins, which would
    // stop the theme persisting there and defeat the server-side pre-paint (issue #116).
    const secure =
      typeof location !== "undefined" && location.protocol === "https:"
        ? "; secure"
        : "";
    document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax${secure}`;
  }
}
